import { createSelector } from '@ngrx/store';
import { Option } from '@sapphire/result';

import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { ToastService } from '@ps/core-overlay';
import { SurveyMarginFormState, Survey } from '@ps/survey-utils';

import {
  SubmittableForm,
  SurveyAudienceForm,
  SurveyLaunchSubmissionForm,
} from '../../../utils';
import {
  selectMarginForm,
  selectSurveyFormState,
} from '../../audience/survey-creation-audience.selectors';
import { selectLaunchForm } from '../../launch/survey-creation-launch.selectors';
import { marginGateForm } from '../../audience/domain/margin-snapshot';

// Names the locale a Traditional MC message is about.
export function traditionalMcLocaleMessage(
  message: string,
  survey: Survey,
): string {
  return `${message} to ${survey.countryName}-${survey.languageName} Survey`;
}

// A launch must validate the launch form even when untouched — its defaults
// (e.g. an empty live URL) are exactly what launching would ship.
export interface SaveGateOptions {
  readonly alwaysGateLaunchForm: boolean;
}

export const DEFAULT_SAVE_GATE: SaveGateOptions = {
  alwaysGateLaunchForm: false,
};

// No child-count condition here, unlike the create gate: this runs only
// once `surveyId` is present, and a pre-draft locale has none. A saved MC
// family does have several children, and saving one of its locales is
// exactly what the buyer means.
export function isSaveable(
  audienceForm: SurveyAudienceForm,
  launchForm: SurveyLaunchSubmissionForm,
  marginForm: SurveyMarginFormState,
  gate: SaveGateOptions = DEFAULT_SAVE_GATE,
): boolean {
  return saveGateForms(audienceForm, launchForm, marginForm, gate).every(
    (form) => form.valid,
  );
}

// A launch form the user never opened holds defaults, not a rejection, so it
// only gates the save — and only toasts — once it has actually been edited.
export function saveGateForms(
  audienceForm: SurveyAudienceForm,
  launchForm: SurveyLaunchSubmissionForm,
  marginForm: SurveyMarginFormState,
  { alwaysGateLaunchForm }: SaveGateOptions = DEFAULT_SAVE_GATE,
): SubmittableForm[] {
  const gatesLaunchForm = alwaysGateLaunchForm || launchForm.isDirty;
  return [
    ...(gatesLaunchForm ? [audienceForm, launchForm] : [audienceForm]),
    marginGateForm(marginForm),
  ];
}

export const SURVEY_NOT_READY_TO_LAUNCH_MESSAGE =
  'Survey is not ready to launch';

export const selectLaunchPrerequisiteError = createSelector(
  selectSurveyFormState,
  selectMarginForm,
  (formState, marginForm): string | null => {
    const rejectingForm = [
      SurveyAudienceForm.from(formState),
      marginGateForm(marginForm),
    ].find((form) => !form.valid);
    return rejectingForm
      ? (rejectingForm.error ?? SURVEY_NOT_READY_TO_LAUNCH_MESSAGE)
      : null;
  },
);

export const selectIsSurveySaveable = createSelector(
  selectSurveyFormState,
  selectLaunchForm,
  selectMarginForm,
  (formState, launchFormState, marginForm) =>
    isSaveable(
      SurveyAudienceForm.from(formState),
      SurveyLaunchSubmissionForm.from(launchFormState),
      marginForm,
    ),
);

// Creating passes true to stay quiet until the required metadata is filled
// in; saving reports whatever is already invalid. Only the first blocking
// error is toasted, so the UX is one toast regardless of which form failed.
export function reportFormErrors(
  toastService: ToastService,
  forms: SubmittableForm[],
  suppressIncompleteErrors = false,
): void {
  Option.from(forms.find((form) => form.showErrors(suppressIncompleteErrors)))
    .andThen((form) => Option.from(form.error))
    .inspect((message) => toastService.error(message, { keepOpen: false }));
}

export function actingUser(authSelectors: AuthSelectorsFacade): {
  id: number;
  companyId: number;
} {
  const user = authSelectors.user().unwrap();
  return {
    id: user.id,
    companyId: authSelectors.activeCompanyId(),
  };
}
